
import Vue, { PropType } from 'vue';
import {
	Runnit,
	RUNNIT_TYPE,
	RunnitNode,
	RUNNITS_ACCESS_LEVEL,
} from '@run-diffusion/shared';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import _keys from 'lodash/keys';
import _find from 'lodash/find';
import { mapActions, mapGetters, mapState } from 'vuex';
import GlassButton from '@/components/base/GlassButton.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import RunnitSettingsDrawer from '@/views/Runnits/RunnitSettingsDrawer.vue';
import ComplexBackground from '@/components/designElements/ComplexBackground.vue';
import ImageGalleryDialog from '@/components/ImageGallery/ImageGalleryDialog.vue';
import SelectedImagesDialog from '@/components/ImageGallery/SelectedImagesDialog.vue';
import {
	MyBalanceMixin,
	RunnitRemixMixin,
	TeamUserMixin,
	RunnitsCRUDMixin,
} from '@/mixins';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import RunnitImage from './RunnitImage.vue';
import ToolOnboardingTopCard from '@/components/ImageGallery/ToolOnboardingTopCard.vue';
import NoImagesEmptyState from '@/assets/NoImagesEmptyState.vue';
import moment from 'moment';
import { RUNNITS_OWNER_SELECTION } from './constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import * as amplitude from '@amplitude/analytics-browser';

export default Vue.extend({
	name: 'RunnitWorkflow',
	metaInfo () {
		return {
			title: this.currentRunnit ? this.currentRunnit.title : 'Loading...',
		};
	},
	props: {
		currentRunnit: {
			type: Object as PropType<Runnit>,
			required: true,
		},
	},
	data () {
		return {
			// Freeze static objects to prevent unnecessary reactivity
			NODE_TOOLBAR_ICON_SIZE: 32,
			expandedNodesMap: {} as Record<string, boolean>,

			toolOnboardingDialog: {
				open: false,
				selectedNode: null as RunnitNode | null,
			},

			visibleNodesMap: {} as Record<string, boolean>,

			// Refresh counter to force ImageGallery to reload when template is edited
			imageGalleryRefreshKey: 0,
		};
	},
	mixins: [
		RunnitsImageSelectMixin,
		TeamUserMixin,
		MyBalanceMixin,
		RunnitRemixMixin,
		RunnitsCRUDMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'runnitNodes',
			'runnitNodesMap',
			'triggers',
		]),
		...mapGetters([
			'runnitNodesLimitMap',
		]),
		selectedNode (): RunnitNode | null {
			return this.runnitState.selectedNodeId ? this.runnitNodesMap[this.runnitState.selectedNodeId] : null;
		},
		computedDescriptionTruncateLength (): number {
			return this.$vuetify.breakpoint.xsOnly ? 30 : 75;
		},
	},
	watch: {
		'$route.params.runnitId': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (this.$vuetify.breakpoint.xsOnly) {
					this.NODE_TOOLBAR_ICON_SIZE = 26;
				}
			},
		},
		runnitNodes: {
			immediate: true,
			handler (newVal: RunnitNode[], oldVal: RunnitNode[]) {
				const newlyCreatedNodes: RunnitNode[] = oldVal && oldVal.length
					? (newVal || []).filter(({ id }) => (
						!_find(oldVal || [], ['id', id])
					))
					: [];
				if (newlyCreatedNodes.length) {
					this.expandedNodesMap = {
						...this.expandedNodesMap,
						...newlyCreatedNodes.reduce((map: Record<string, boolean>, { id }) => ({
							...map,
							[id]: true,
						}), {}),
					};
					this.onSelectNode(newlyCreatedNodes[0].id);
				} else if (
					newVal &&
					newVal.length &&
					this.$vuetify.breakpoint.mdAndUp &&
					!this.runnitState.selectedNodeId
				) {
					this.onSelectNode(newVal[0].id);
				} else if (!newVal?.length && oldVal?.length) {
					this.onSelectNode(null);
				}
			},
		},
		runnitNodesLimitMap: {
			immediate: true,
			handler (newVal: Record<string, RunnitNode>, oldVal: Record<string, RunnitNode>) {
				if (newVal !== oldVal && _isEmpty(this.expandedNodesMap)) {
					this.initExpandedNodesMap(newVal);
				}
			},
		},
		selectedNode: {
			immediate: true,
			handler (newVal: RunnitNode) {
				const newNodeDefTitle = newVal?.nodeDef?.title;
				const currentToolName = this.$route.query.toolName;
				if (newNodeDefTitle && currentToolName !== newNodeDefTitle) {
					this.$router.replace({
						...this.$route,
						query: {
							...this.$route.query,
							toolName: newNodeDefTitle,
						},
					});
				}
			},
		},
		'triggers.toggleRunnitWorkflowAllToolsTrigger': {
			immediate: false,
			handler (
				newVal: { expandAll?: boolean, collapseAll?: boolean, trigger: number },
				oldVal: { expandAll?: boolean, collapseAll?: boolean, trigger: number },
			) {
				if (newVal.trigger !== oldVal?.trigger) {
					if (newVal.expandAll) {
						// Open all tools
						this.initExpandedNodesMap(this.runnitNodesLimitMap);
					} else if (newVal.collapseAll) {
						// Collapse all tools
						this.expandedNodesMap = {};
					}
				}
			},
		},
		'runnitState.createRunnitTemplateOpen': {
			immediate: false,
			handler (newVal: boolean, oldVal: boolean) {
				// When template dialog closes after editing, refresh ImageGallery components
				// by incrementing the refresh key, which forces them to remount and reload data
				if (oldVal && !newVal && this.currentRunnit?.type === RUNNIT_TYPE.TEMPLATE) {
					this.imageGalleryRefreshKey += 1;
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
			'setUpsellDialog',
		]),
		moment,
		isSelectedNodeDrawerOpen (nodeId: string) {
			return !!(
				this.runnitState.selectedNodeId &&
				this.runnitState.selectedNodeId === nodeId &&
				this.runnitState.settingsDrawerOpen
			);
		},
		isNodeExpanded (nodeId: string) {
			return !!(this.expandedNodesMap[nodeId]);
		},
		initExpandedNodesMap (runnitNodesLimitMap: Record<string, RunnitNode>) {
			this.expandedNodesMap = {};
			_keys(runnitNodesLimitMap || {}).forEach((nodeId: string) => {
				if (runnitNodesLimitMap[nodeId]) this.expandedNodesMap[nodeId] = true;
			});
		},
		onSelectNode (nodeId: string) {
			this.updateRunnitState({
				selectedNodeId: nodeId,
				settingsDrawerOpen: !!nodeId,
			});
		},
		setSettingsDrawerOpen (isOpen: boolean) {
			this.updateRunnitState({
				settingsDrawerOpen: isOpen,
				...(!isOpen && {
					imageGalleryDialogOpen: false,
					promptGeneratorOpen: false,
					imageSelectOpen: false,
					inpaintingEditorOpen: false,
				}),
			});
		},
		toggleNodeExpanded (event: any, nodeId: string) {
			event.stopPropagation();
			this.expandedNodesMap[nodeId] = !this.expandedNodesMap[nodeId];
			this.$forceUpdate();
		},
		openToolOnboardingDialog (node: RunnitNode) {
			this.toolOnboardingDialog = {
				open: true,
				selectedNode: node,
			};
		},
		closeToolOnboardingDialog () {
			this.toolOnboardingDialog = {
				open: false,
				selectedNode: null,
			};
		},
		intersectHandler (entries: IntersectionObserverEntry[], nodeId: string) {
			if (entries.length && entries[0].isIntersecting) {
				this.visibleNodesMap = {
					...this.visibleNodesMap,
					[nodeId]: true,
				};
			} else {
				this.visibleNodesMap = {
					...this.visibleNodesMap,
					[nodeId]: false,
				};
			}
		},
		async createBoardFromTemplate (): Promise<void> {
			try {

				// Check if user can create a new runnit (this will show upsell dialog if limit reached)
				if (!this.canCreateNewRunnit(false)) {
					return;
				}

				amplitude.track(`Open Create Board from Template`, { location: 'RunnitTemplate' });

				// Create a draft runnit based on the template
				const clonedRunnit = {
					...this.currentRunnit,
					userId: this.user.id, // Set the current user as owner
					title: `${this.currentRunnit.title} (Copy)`, // Add (Copy) to distinguish from template
					createdAt: new Date(),
					deletedAt: null,
					// Reset template-specific fields
					type: RUNNIT_TYPE.WORKFLOW,
					accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE,
					teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.team?.id : null,
				};
				delete clonedRunnit.id;

				// Update the runnit state to open the edit dialog with the cloned runnit
				this.updateRunnitState({
					runnitDraft: clonedRunnit,
					runnitDraftNodeRef: null,
					configureRunnitOpen: true,
					// Set a flag to indicate this is a template clone
					cloneFromTemplate: true,
					templateRunnit: this.currentRunnit,
				});
			} catch (error) {
				console.error('Error creating board from template:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error creating board from template, please try again',
					show: true,
				});
			}
		},
	},
	components: {
		GlassButton,
		ImageGallery,
		EmptyState,
		ComplexBackground,
		ImageGalleryDialog,
		SelectedImagesDialog,
		RunnitSettingsDrawer,
		RunnitImage,
		ToolOnboardingTopCard,
		NoImagesEmptyState,
	},
});
